import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useDeepCompareEffect from "use-deep-compare-effect";
import { z } from "zod";

import type { DraftPayment } from "@/apis/gql/types";
import { DatePickerInput } from "@/components/shared/form/date-picker-input";
import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { Form as FormProvider } from "@/components/ui/form";
import {
  type GetInvoiceDetailsInvoice,
  INVOICE_QUERY,
  type InvoiceDetailsDraftPayment,
} from "@/queries/invoices/get";
import { useCreateDraftPayment } from "@/queries/payments/draft-payments/create-draft-payment";
import { useUpdateDraftPayment } from "@/queries/payments/draft-payments/update-draft-payment";

// Helper function to calculate previous working day (skips weekends)
const getPreviousWorkingDay = (date: Date): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() - 1);

  // If it's Sunday (0) or Saturday (6), go back to Friday
  while (result.getDay() === 0 || result.getDay() === 6) {
    result.setDate(result.getDate() - 1);
  }

  return result;
};

type Props = {
  draftPayments: InvoiceDetailsDraftPayment[];
  invoiceId: string;
  invoice: GetInvoiceDetailsInvoice;
  onValuesChange?: (values: Partial<ExecutionDateFormValues>) => void;
  onFieldFocus: (field: string) => void;
};

export const EditExecutionDateForm = ({
  draftPayments,
  invoice,
  invoiceId,
  onValuesChange,
  onFieldFocus,
}: Props) => {
  const { updateDraftPayment } = useUpdateDraftPayment({
    refetchQueries: [INVOICE_QUERY],
  });
  const [createDraftPayment] = useCreateDraftPayment({
    refetchQueries: [INVOICE_QUERY],
  });
  const handleUpdatePayment = (values: ExecutionDateFormValues) => {
    updateDraftPayment({
      draftPaymentId: values.draftPaymentId,
      executionDate: values.executionDate,
    });
  };

  const handleCreatePayment = (values: ExecutionDateFormValues) => {
    createDraftPayment(invoiceId, {
      executionDate: values.executionDate,
    });
  };

  if (draftPayments.length === 0) {
    return (
      <Form
        key={draftPayments?.[0]?.draftPaymentId}
        index={1}
        values={{
          draftPaymentId: draftPayments?.[0]?.draftPaymentId,
          executionDate: draftPayments?.[0]?.executionDate
            ? new Date(draftPayments?.[0]?.executionDate)
            : null,
          dueDate: invoice.dueDate,
        }}
        onSubmit={handleCreatePayment}
        onValuesChange={onValuesChange}
        onFieldFocus={onFieldFocus}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {draftPayments.map((payment, index) => (
        <Form
          key={payment.draftPaymentId}
          index={index + 1}
          values={{
            draftPaymentId: payment.draftPaymentId,
            executionDate: payment.executionDate
              ? new Date(payment.executionDate)
              : null,
            dueDate: invoice.dueDate,
          }}
          onSubmit={handleUpdatePayment}
          onValuesChange={onValuesChange}
          onFieldFocus={onFieldFocus}
        />
      ))}
    </div>
  );
};

const executionDateFormSchema = z.object({
  draftPaymentId: z.string().optional(),
  executionDate: z
    .date()
    .nullable()
    .optional()
    .refine(
      (date) => !date || date >= new Date(new Date().setHours(0, 0, 0, 0)),
      {
        message: "Ausführungsdatum muss heute oder in der Zukunft liegen",
      },
    ),
  dueDate: z.any().nullable().optional(),
});

type ExecutionDateFormValues = z.infer<typeof executionDateFormSchema>;

type FormProps = {
  values: ExecutionDateFormValues;
  index?: number;
  disabled?: boolean;
  onSubmit: (values: ExecutionDateFormValues) => void;
  onValuesChange?: (values: ExecutionDateFormValues) => void;
  onFieldFocus: (field: string) => void;
};

const Form = ({
  values,
  disabled = false,
  onSubmit,
  onValuesChange,
  index,
  onFieldFocus,
}: FormProps) => {
  const { t } = useTranslation();

  const form = useForm<ExecutionDateFormValues>({
    resolver: zodResolver(executionDateFormSchema),
    defaultValues: values,
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const formValues = form.watch();
  const formState = form.formState;

  const isDirty = formState.isDirty;

  const handleSubmit = async (values: ExecutionDateFormValues) => {
    onSubmit(values);
    form.reset(values);
  };

  const handleDueDateClick = () => {
    if (values.dueDate) {
      const executionDate = getPreviousWorkingDay(new Date(values.dueDate));
      form.setValue("executionDate", executionDate, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    }
  };

  useEffect(() => {
    form.trigger();
  }, []);

  useDeepCompareEffect(() => {
    if (isDirty) {
      onValuesChange?.(formValues);
    }
  }, [formValues, onValuesChange, isDirty]);

  return (
    <FormProvider {...form}>
      <form className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <DatePickerInput
            optional
            clearButton
            label={
              <span>
                {t("payment_details.fields.execution_date")} ({t("due_date")}:{" "}
                {values.dueDate ? (
                  <button
                    type="button"
                    className="text-foreground hover:text-foreground/80 border-b border-dotted border-muted-foreground hover:border-foreground cursor-pointer transition-colors duration-200"
                    onClick={handleDueDateClick}
                    disabled={disabled}
                  >
                    {values.dueDate
                      ? new Date(values.dueDate).toLocaleDateString("de-DE")
                      : ""}
                  </button>
                ) : (
                  ""
                )}
                )
              </span>
            }
            name="executionDate"
            disabled={disabled}
            onFocus={() => onFieldFocus("executionDate")}
          />
        </div>
        <AutoSaveForm onSubmit={handleSubmit} immediate />
      </form>
    </FormProvider>
  );
};

export type EditExecutionDateFormValues = ExecutionDateFormValues;
